/* eslint import/no-unresolved: 0 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { Skeleton } from '@openedx/paragon';
import { useModel } from '@src/generic/model-store';

import { fetchOrg, fetchVerifiedName } from '../../data/lms_api';
import Signatory from './Signatory';
import './CertificatePreview.scss';

const CertificatePreview = ({ courseId, bgColor }) => {
  const { username, name } = getAuthenticatedUser();

  const {
    number,
    org,
    title,
  } = useModel('courseHomeMeta', courseId);

  const platformLogoUrl = getConfig().LOGO_URL;

  const [isLoading, setIsLoading] = useState(true);
  const [orgMetadata, setOrgMetadata] = useState({});
  const [certificateName, setCertificateName] = useState(name);

  useEffect(() => {
    fetchOrg(org).then((data) => {
      setOrgMetadata(data);
    });

    fetchVerifiedName(username).then((data) => {
      if (data) {
        const certName = data.useVerifiedNameForCerts ? data.verifiedName : data.profileName;
        setCertificateName(certName);
      }
    });

    setIsLoading(false);
  }, [org, username, name]);

  const CertificatePreviewComponent = () => (
    <div className={`certificate-preview border m-4 p-3 ${bgColor}`}>
      <div className="d-flex justify-content-between" data-testid="certificate-preview-header">
        <img
          alt="Verified Certificate logo"
          className="verified-certificate-logo"
          src="https://edxuploads.s3.amazonaws.com/certificate_template_assets/122/verified-certificate-logo.svg"
        />
        <span className="sr-only">Verified Certificate</span>
        <div className="mb-4">
          <div className="sr-only">Supported by the following organizations</div>
          {orgMetadata?.logo
            ? (
              <img
                alt={`${orgMetadata.shortName} logo`}
                className="organization-logo"
                data-testid="organization-logo"
                src={orgMetadata.logo}
              />
            )
            : (<div data-testid="organization-short-name">{orgMetadata.shortName}</div>)}
        </div>
      </div>
      <div className="d-flex justify-content-between">
        <div className="certificate-statement">
          <div className="certificate-statement-container d-flex flex-column">
            <span
              className="certificate-statement-detail"
            >
              This is to certify that
            </span>
            <strong
              className="certificate-statement-detail certificate-recipient font-weight-bold"
            >
              {certificateName}
            </strong>
            <span
              className="certificate-statement-detail"
            >
              successfully completed and received a passing grade in
            </span>
            <span
              className="certificate-course font-weight-bold"
            >
              <span>{number}</span>:
              <span>{title}</span>
            </span>
            <span
              className="certificate-statement-detail"
            >
              a course of study offered by {orgMetadata.shortName},
              an online learning initiative of {orgMetadata.name}.
            </span>
          </div>
        </div>
        <div className="signatories">
          <div className="sr-only">Noted by</div>
          <Signatory name="Maurilio Pugliesi" />
          <Signatory name="Justine Doe, Ph.D." />
        </div>
      </div>
      <div className="certificate-identifiers">
        <ul className="d-flex no-bullets stamps pt-3">
          <li className="stamp">
            <img className="platform-logo" src={platformLogoUrl} alt="edX" />
          </li>
          <li className="stamp d-flex flex-column">
            <span className="title">Verified Certificate</span>
            <span className="emphasized">Issued Month / Year</span>
          </li>
          <li className="stamp d-flex flex-column">
            <span className="title">Valid Certificate ID</span>
            <span className="emphasized">12345678901234567890</span>
          </li>
        </ul>
      </div>
      <div className="preview-watermark">
        <span>PREVIEW</span>
      </div>
    </div>
  );

  // We can render if the call to the verified name endpoint returns a null, because we fall back to their LMS
  // profile name, but if the call to the organization endpoint fails, we cannot render the certificate because
  // we require data from this endpoint.
  let component;

  if (!orgMetadata) {
    component = false;
  } else if (isLoading) {
    component = (
      <Skeleton
        count={1}
        height="5rem"
      />
    );
  } else {
    component = (
      <CertificatePreviewComponent />
    );
  }

  return (
    component
  );
};

CertificatePreview.propTypes = {
  courseId: PropTypes.string.isRequired,
};

export default CertificatePreview;
